<template>
  <div
    class="card"
    :class="{ 'is-confirm-card': !isOwnStep }"
    v-show="isSection('create-date')"
  >
    <div class="overflow-scroll-y">
      <!--
        確認画面に取り込まれた書式では、リード文とメール便バナーは確認画面側
        （RirekishoEdit / FormatEdit）が持ち、ここは基本情報と同じカード表現にする。
        作成日が独立ステップの書式では従来どおりステップの見出しを出す。
      -->
      <div class="card-header">
        <template v-if="isOwnStep">
          <h2>履歴書の作成日を入力</h2>
          <p>実際に書類を持参する日、または郵送する日を選びましょう</p>
        </template>
        <h3 v-else>作成日</h3>
      </div>
      <div class="card-content">
        <div class="edit-form-list">
          <div class="create-date-action">
            <div class="action-item">
              <el-button
                class="button-block"
                :disabled="isTodaySelected"
                @click="setToday"
                >今日の日付を入力</el-button
              >
            </div>
            <div class="action-item">
              <el-button
                class="button-block"
                :disabled="isTomorrowSelected"
                @click="setTomorrow"
                >明日の日付を入力</el-button
              >
            </div>
            <div class="action-item">
              <el-date-picker
                v-model="formatData.createDate"
                type="date"
                :clearable="false"
                placeholder="選択してください"
                :format="'yyyy年M月d日'"
              />
            </div>
          </div>
        </div>
        <br />
        <div class="aiInterview_date" v-if="showBanner">
          <a
            href="/yagiprimequickemail/info"
            class="aiInterview_lead"
            target="_blank"
            id="quickemailbnr_date_btn"
          >
            <img src="/static/mypage/quickemailbnr.jpg" alt="メール便" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useRirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'
import { isCardSectionActive } from '@/utils/format-edit/cardSections'

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    formatId?: string
    showQuickEmailBanner?: boolean
  }>(),
  {
    showQuickEmailBanner: true
  }
)

type DateFormLib = {
  dateWithoutTime: (date: Date | string | null | undefined) => number | string
  getToday: () => Date
  getTommorow: () => Date
}

type GlobalVarWithPayments = {
  in_app_payments?: boolean
}

const { formLib: formLibRaw, globalVar } = useHostServices()
const formLib = formLibRaw as typeof formLibRaw & DateFormLib
const formatData = useRirekishoFormatData()

/**
 * 作成日は書式によって独立ステップではなく確認画面に置かれる
 * （カード定義の `sections`）。
 */
const isSection = (section: string) =>
  isCardSectionActive(props.formatId, props.currentCardId, section)

/** 作成日が独立ステップか（確認画面に取り込まれていないか） */
const isOwnStep = computed(() => props.currentCardId === 'create-date')

const showBanner = computed(
  () =>
    isOwnStep.value &&
    props.showQuickEmailBanner &&
    !!(globalVar as typeof globalVar & GlobalVarWithPayments).in_app_payments
)

const isTodaySelected = computed(
  () =>
    formLib.dateWithoutTime(formatData.createDate) ===
    formLib.dateWithoutTime(formLib.getToday())
)

const isTomorrowSelected = computed(
  () =>
    formLib.dateWithoutTime(formatData.createDate) ===
    formLib.dateWithoutTime(formLib.getTommorow())
)

function setToday() {
  formatData.createDate = formLib.getToday()
}

function setTomorrow() {
  formatData.createDate = formLib.getTommorow()
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/format-edit/document-card';

// 確認画面に取り込まれたときは基本情報ステップと同じカード表現にそろえる
.card.is-confirm-card {
  @include document-card-frame;

  // 基本情報ステップ（.basic-info-sections.is-cards の左右 1rem）と同じ内側寄せ。
  // 共通の .card は width: 100% のため、margin だけでは右へはみ出す。auto に戻す。
  width: auto;
  margin-right: 1rem;
  margin-left: 1rem;

  .card-header {
    @include document-card-header;
  }

  .card-content {
    @include document-card-body;
  }
}

/*
 * 共通スタイルの `.card-header + .card-content { border-top: ... }` は
 * ステップによって隣接する / しないで出方が分かれてしまう。
 * このフローでは「出さない」側（多数派）にそろえる。
 */
.card-header + .card-content {
  border-top: none;
}

.aiInterview_date {
  text-align: center;

  .aiInterview_lead {
    img {
      width: 350px;
      border-radius: 10px;

      @media screen and (width <= 599px) {
        width: 100%;
      }
    }
  }
}
</style>
